import ServiceClient from "src/utils/extension/ServiceClient";
import { getPoshmarkCookies } from "src/utils/extension/poshmark";

const PMVersion = "2024.34.0";
const AppVersion = "5.4.0";

export class NotLoggedError extends Error {
  constructor() {
    super("You must be logged in Poshmark to perform the sync process.");
  }
}

export type PoshmarkApiError = {
  errorType?: string | null;
  params?: unknown;
  pmErrorCode?: string | null;
  userMessage?: string | null;
  stackTrace?: unknown;
  errorMessage?: string | null;
  statusCode?: number | null;
};

type PoshmarkFetchResponse = Awaited<
  ReturnType<typeof ServiceClient.prototype.fetch>
>;

export const PoshmarkSessionExpiredUserMessage =
  "Your Poshmark session has expired. Please open poshmark.com, log in again, then retry.";

const GenericPoshmarkRequestUserMessage =
  "Poshmark didn't send all the requested data. Please retry the import.";

export class PoshmarkRequestError extends Error {
  readonly method: string;
  readonly status: number;
  readonly url: string;
  readonly error?: PoshmarkApiError;
  readonly body?: unknown;

  constructor(
    method: string,
    status: number,
    url: string,
    message?: string,
    opts?: { error?: PoshmarkApiError; body?: unknown }
  ) {
    const detailParts = [message];
    if (opts?.error?.errorType) detailParts.push(opts.error.errorType);
    if (opts?.error?.userMessage) detailParts.push(opts.error.userMessage);
    const detail = detailParts.filter(Boolean).join(" — ");
    super(
      `Poshmark request failed [${method}] ${url} returned ${status}: ${detail}`
    );
    this.name = "PoshmarkRequestError";
    this.status = status;
    this.method = method;
    this.url = url;
    this.error = opts?.error;
    this.body = opts?.body;
  }

  static fromResponse(
    response: PoshmarkFetchResponse,
    message: string,
    opts?: { error?: PoshmarkApiError; body?: unknown }
  ): PoshmarkRequestError {
    return new PoshmarkRequestError(
      response.request.init.method,
      response.status,
      response.request.url,
      message,
      opts
    );
  }
}

export class PoshmarkRequestSessionExpiredError extends PoshmarkRequestError {
  constructor(
    method: string,
    status: number,
    url: string,
    label?: string,
    opts?: { error?: PoshmarkApiError; body?: unknown }
  ) {
    super(method, status, url, label, opts);
    this.name = "PoshmarkRequestSessionExpiredError";
  }

  static fromResponse(
    response: PoshmarkFetchResponse,
    message: string,
    opts?: { error?: PoshmarkApiError; body?: unknown }
  ): PoshmarkRequestSessionExpiredError {
    return new PoshmarkRequestSessionExpiredError(
      response.request.init.method,
      response.status,
      response.request.url,
      message,
      opts
    );
  }
}

function tryParseResponseBody(data: unknown): unknown {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}

function getPoshmarkApiError(body: unknown): PoshmarkApiError | undefined {
  if (!body || typeof body !== "object") return undefined;
  const error = (body as { error?: unknown }).error;
  if (!error || typeof error !== "object") return undefined;
  return error as PoshmarkApiError;
}

function isSessionExpiredError(error?: PoshmarkApiError): boolean {
  if (!error) return false;
  if (error.errorType === "ExpiredSignupToken") return true;
  const statusCode = error.statusCode;
  return statusCode === 401 || statusCode === 403;
}

function throwPoshmarkRequestError(
  res: PoshmarkFetchResponse,
  label: string
): never {
  const body = tryParseResponseBody(res.data);
  const apiError = getPoshmarkApiError(body);
  const opts = { error: apiError, body };
  if (isSessionExpiredError(apiError)) {
    throw PoshmarkRequestSessionExpiredError.fromResponse(res, label, opts);
  }
  throw PoshmarkRequestError.fromResponse(res, label, opts);
}

export function poshmarkUserFacingErrorMessage(e: unknown): string {
  if (e instanceof PoshmarkRequestSessionExpiredError) {
    return PoshmarkSessionExpiredUserMessage;
  }
  if (e instanceof PoshmarkRequestError) {
    if (e.error?.userMessage) return String(e.error.userMessage);
    return GenericPoshmarkRequestUserMessage;
  }
  if (e instanceof Error) return e.message || e.toString();
  return String(e);
}

export function logPoshmarkError(e: unknown): void {
  if (e instanceof NotLoggedError) {
    console.warn(e);
    return;
  }
  if (e instanceof PoshmarkRequestError) {
    console.error(e, {
      method: e.method,
      status: e.status,
      url: e.url,
      error: e.error,
      body: e.body,
    });
    return;
  }
  console.error(e);
}

export type FetchInventoryStatus =
  | "all"
  | "available"
  | "coming_soon"
  | "sold_out";

export interface FetchInventoryParams {
  summarize?: boolean;
  appVersion?: string;
  pmVersion?: string;
  maxId?: string;
  count?: number;
  sortBy?: string;
  inventoryStatus?: FetchInventoryStatus[];
}

class PoshmarkClient extends ServiceClient {
  async getUserId() {
    const cookies = await getPoshmarkCookies(this.extensionId);
    const jwt = cookies.find((c) => c.name === "jwt")?.value;
    if (!jwt) throw new NotLoggedError();
    try {
      const userId = JSON.parse(atob(jwt.split(".")[1])).user_id;
      return userId;
    } catch (e) {
      throw new Error("Invalid poshmark JWT", e as Error);
    }
  }

  async fetchUser(uid: string) {
    const res = await this.fetchWithRetry(
      `https://poshmark.com/vm-rest/users/${uid}`,
      {
        headers: {
          Accept: "application/json",
        },
        method: "GET",
      }
    );

    if (!res.ok) throwPoshmarkRequestError(res, `fetchUser(${uid})`);

    const json = tryParseResponseBody(res.data) as any;

    if (json?.error || !json?.data)
      throwPoshmarkRequestError(res, `fetchUser(${uid})`);

    return json;
  }

  async fetchSales(
    uid: string,
    {
      maxId,
      pmVersion = PMVersion,
      boughtDate,
      status,
    }: {
      maxId?: string;
      pmVersion?: string;
      boughtDate?: string;
      status?: string[];
    } = {}
  ) {
    const request = {
      filters: {
        // bought_date: { start: "2023-08-04T00:00:00-03:00", end: "2023-10-04T00:00:00-03:00" }
        ...(boughtDate ? { bought_date: boughtDate } : {}),
        ...(status ? { status } : {}),
      },
      ...(maxId ? { max_id: maxId } : {}),
    };
    const params = {
      request: JSON.stringify(request),
      pm_version: pmVersion,
    };

    const url = `https://poshmark.com/vm-rest/users/${uid}/orders/sales?${Object.entries(
      params
    )
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join("&")}`;
    const res = await this.fetchWithRetry(url, {
      headers: {
        Accept: "application/json",
      },
      method: "GET",
    });

    if (!res.ok) throwPoshmarkRequestError(res, "fetchSales");

    const json = tryParseResponseBody(res.data) as any;

    if (json?.error) throwPoshmarkRequestError(res, "fetchSales");

    return json;
  }

  async *fetchSalesFull(poshmarkUserId: string, extra = {}) {
    let maxId;
    do {
      const sales = await this.fetchSales(poshmarkUserId, { ...extra, maxId });

      yield {
        sales: sales.data.sales_summary,
        meta: sales.meta,
        response: sales,
      };

      maxId =
        sales.meta && sales.meta.next_max_id ? sales.meta.next_max_id : null;
    } while (maxId);
  }

  async fetchSaleDetail(saleId: string) {
    const url = `https://poshmark.com/order/sales/${saleId}`;
    const res = await this.fetchWithRetry(url, {
      headers: {
        Accept: "*/*",
      },
      method: "GET",
    });

    if (!res.ok) throwPoshmarkRequestError(res, `fetchSaleDetail(${saleId})`);

    let text = res.data;
    let needle = "<script>window.__INITIAL_STATE__=";
    let index = text.indexOf(needle);
    text = text.substring(index + needle.length);
    index = text.indexOf("</script>");
    text = text.substring(0, index);
    needle = "};";
    index = text.indexOf(needle);
    text = text.substring(0, index + needle.length - 1);
    return JSON.parse(text).$_order_details;
  }

  async fetchInventoryItem(
    id,
    { appVersion = AppVersion, pmVersion = PMVersion } = {}
  ) {
    const params = {
      app_version: appVersion,
      pm_version: pmVersion,
    };
    const res = await this.fetchWithRetry(
      `https://poshmark.com/vm-rest/posts/${id}?${Object.entries(params)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&")}`,
      {
        headers: {
          Accept: "application/json",
        },
        method: "GET",
      }
    );
    const json = res.data;

    return json;
  }

  async fetchInventory(
    username: string,
    {
      summarize = true,
      appVersion = AppVersion,
      pmVersion = PMVersion,
      maxId,
      count = 50,
      inventoryStatus = ["all"],
      sortBy,
    }: FetchInventoryParams = {}
  ) {
    const request = {
      filters: {
        department: "All",
        inventory_status: inventoryStatus,
      },
      experience: "all",
      ...(maxId ? { max_id: maxId } : {}),
      ...(sortBy ? { sort_by: sortBy } : {}),
      count,
    };
    // summarize=true&app_version=2.55&pm_version=2023.38.0
    const params = {
      request: JSON.stringify(request),
      ...(summarize ? { summarize: true } : {}),
      app_version: appVersion,
      pm_version: pmVersion,
    };

    const res = await this.fetchWithRetry(
      `https://poshmark.com/vm-rest/users/${username}/posts/filtered?${Object.entries(
        params
      )
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&")}`,
      {
        headers: {
          Accept: "application/json",
          Referer: `https://poshmark.com/closet/${username}`,
        },
        method: "GET",
      }
    );

    if (!res.ok) throwPoshmarkRequestError(res, "fetchInventory");

    const json = tryParseResponseBody(res.data) as any;
    if (json?.error) throwPoshmarkRequestError(res, "fetchInventory");

    return json;
  }

  async *fetchInventoryFull(
    username: string,
    params: FetchInventoryParams = {}
  ) {
    const items = [];
    let maxId;
    do {
      const inventory = await this.fetchInventory(username, {
        ...params,
        maxId,
      });

      yield {
        items: inventory.data,
        response: inventory,
      };
      maxId =
        inventory.more && inventory.more.next_max_id
          ? inventory.more.next_max_id
          : null;
    } while (maxId);

    return items;
  }
}

export default PoshmarkClient;
